import React, { useState, useCallback } from 'react';
import { translate } from 'react-multi-lang';
import { DownloadOutlined, UploadOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';

import { Row, Col, Button, Upload, Table, Progress, Space, Divider, Alert, message, Tag, Spin } from 'antd';
import moment from 'moment';
import 'moment/locale/ca';
import 'moment/locale/es';

import * as XLSX from 'xlsx';
import { AllHtmlEntities } from 'html-entities';

import { Api } from '../../../../helpers';

const entities = new AllHtmlEntities();

// ---------- Constants ----------
// Columnes del full (totes s'envien al POST; no hi ha columna id perquè només inserts)
const COLUMNES = [
  'id_rebut',
  'import',
  'concepte',
  'tipus_pagament',
  'data_pagament'
];

const TIPUS_PAGAMENT_VALIDS = ['BANC', 'EFECTIU', 'TRANSFERENCIA', 'XEC_GUARDERIA', 'TARGETA'];

// Epoch d'Excel: serial 0 = 1899-12-30
const EXCEL_EPOCH = moment.utc('1899-12-30', 'YYYY-MM-DD', true);

// ---------- Helpers ----------
const esEnter = (v) => v !== null && v !== undefined && v !== '' && Number.isInteger(Number(v)) && !isNaN(Number(v));
const esNumeric = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));

function normalitzarFila(fila){
  // Passa totes les claus a lowercase + trim
  const nova = {};
  Object.keys(fila).forEach((k) => {
    nova[String(k).trim().toLowerCase()] = fila[k];
  });
  return nova;
}

// Converteix el valor de data_pagament a moment UTC:
//  - Serial Excel (número) → dies des de 1899-12-30
//  - String 'DD/MM/YYYY' (o 'DD/MM/YYYY HH:mm', 'YYYY-MM-DD') → parseig estricte
//  - Date (defensiu) → components UTC
// Retorna null si no és vàlid
const parseDataPagament = (v) => {
  if(v === null || v === undefined || v === '') return null;
  if(v instanceof Date && !isNaN(v.getTime())){
    return moment.utc(Date.UTC(v.getUTCFullYear(), v.getUTCMonth(), v.getUTCDate(), v.getUTCHours(), v.getUTCMinutes(), v.getUTCSeconds()));
  }
  if(typeof v === 'number' && !isNaN(v) && v > 0){
    const dies = Math.floor(v);
    const fraccio = v - dies;
    const segons = Math.round(fraccio * 86400);
    return EXCEL_EPOCH.clone().add(dies, 'days').add(segons, 'seconds');
  }
  if(typeof v === 'string'){
    const s = v.trim();
    const m1 = moment.utc(s, 'DD/MM/YYYY', true);
    if(m1.isValid()) return m1;
    const m2 = moment.utc(s, 'DD/MM/YYYY HH:mm', true);
    if(m2.isValid()) return m2;
    const m3 = moment.utc(s, 'YYYY-MM-DD', true);
    if(m3.isValid()) return m3;
    return null;
  }
  return null;
};

function validarFila(fila, mapRebuts){
  const errors = [];
  const avisos = [];

  // Presència
  COLUMNES.forEach((c) => {
    if(fila[c] === undefined || fila[c] === null || fila[c] === ''){
      errors.push(`Falta '${c}'`);
    }
  });
  if(errors.length > 0) return { errors, avisos };

  // id_rebut: enter > 0 i existent a la BD
  if(!esEnter(fila.id_rebut) || Number(fila.id_rebut) <= 0){
    errors.push("id_rebut ha de ser un enter > 0");
  }else{
    const idRebut = Number(fila.id_rebut);
    if(!(idRebut in mapRebuts)){
      errors.push(`No s'ha pogut comprovar el rebut ${idRebut}`);
    }else if(mapRebuts[idRebut] === null){
      errors.push(`No s'ha pogut comprovar el rebut ${idRebut} (error de connexió o rebut inexistent)`);
    }else if(!mapRebuts[idRebut]){
      errors.push(`id_rebut=${idRebut} no existeix a la BD`);
    }
  }

  // import: numèric >= 0
  if(!esNumeric(fila.import) || Number(fila.import) < 0){
    errors.push("import ha de ser numèric >= 0");
  }

  // concepte: no buit
  if(!String(fila.concepte).trim()){
    errors.push("concepte no pot ser buit");
  }

  // tipus_pagament: enum
  if(!TIPUS_PAGAMENT_VALIDS.includes(String(fila.tipus_pagament).trim().toUpperCase())){
    errors.push(`tipus_pagament ha de ser un de: ${TIPUS_PAGAMENT_VALIDS.join(', ')}`);
  }

  // data_pagament: data vàlida
  if(!parseDataPagament(fila.data_pagament)){
    errors.push("data_pagament ha de ser una data vàlida (DD/MM/YYYY)");
  }

  return { errors, avisos };
}

// ---------- Component ----------
function EntregatACompteImportar(props){

  const [files, setFiles] = useState([]);              // files parsejades + validació
  const [nomFitxer, setNomFitxer] = useState(null);
  const [importantEstat, setImportantEstat] = useState('idle'); // idle|processant|acabat
  const [progres, setProgres] = useState(0);
  const [resultats, setResultats] = useState([]);      // per fila: {fila, ok, id_rebut, id_retorn, missatge}

  // mapRebuts: { [id_rebut]: true|false } (existeix o no el rebut; només resultats fiables)
  const [mapRebuts, setMapRebuts] = useState({});
  const [carregantRebuts, setCarregantRebuts] = useState(false);

  // ---------- Comprovació d'existència del rebut ----------
  // Retorna: true (existeix) | false (comprovat, no existeix) | null (no s'ha pogut comprovar)
  // ATENCIÓ: l'endpoint pot retornar success=1 amb un rebut buit ({} o []) quan no existeix,
  // per això es verifica que el rebut retornat tingui un id coherent amb el demanat.
  const comprovarRebut = useCallback(async (idRebut) => {
    try{
      const resposta = await Api.get('/rebut/header/' + idRebut);
      if(resposta && resposta.result && resposta.result.success === 1 && resposta.data){
        const rebut = resposta.data.rebut;
        // Pot venir com a objecte (fetch) o array (fetchAll)
        const fila = Array.isArray(rebut) ? rebut[0] : rebut;
        const rebutId = fila ? fila.id : undefined;
        return rebutId !== undefined && rebutId !== null && Number(rebutId) === Number(idRebut);
      }
      return false;
    }catch(err){
      console.error('Error comprovant rebut ' + idRebut, err);
      return null;
    }
  }, []);

  // ---------- Descarrega de la plantilla ----------
  const descarregarPlantilla = () => {
    const capceleres = COLUMNES;
    const exemple = [
      123,                      // id_rebut
      681.50,                   // import
      'Quota 1r trimestre',     // concepte
      'TRANSFERENCIA',          // tipus_pagament
      '17/07/2026'              // data_pagament
    ];
    const wsEntregat = XLSX.utils.aoa_to_sheet([capceleres, exemple]);

    const instruccions = [
      ['Instruccions per omplir la plantilla'],
      [''],
      ['1. Cada fila crea UNA entrada "entregat a compte" per al rebut indicat (només inserts).'],
      ['2. No canviïs els noms de les columnes.'],
      ['3. Totes les columnes són obligatòries.'],
      [''],
      ['Detall de columnes:'],
      ['id_rebut       -> ID del rebut existent al sistema'],
      ['import         -> import lliurat (numèric >= 0)'],
      ['concepte       -> text descriptiu'],
      ['tipus_pagament -> BANC / EFECTIU / TRANSFERENCIA / XEC_GUARDERIA / TARGETA'],
      ['data_pagament  -> data en format DD/MM/YYYY'],
      [''],
      ['Notes:'],
      ['- El rebut ha d\'existir al sistema (es comprova abans d\'importar).'],
      ['- Es poden fer diverses entrats per al mateix rebut.'],
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instruccions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsEntregat, 'EntregatCompte');
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instruccions');
    XLSX.writeFile(wb, 'plantilla_entregat_compte.xlsx');
  };

  // ---------- Lectura del fitxer ----------
  const llegirFitxer = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try{
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheetName = wb.SheetNames.includes('EntregatCompte') ? 'EntregatCompte' : wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const filesRaw = XLSX.utils.sheet_to_json(sheet, { defval: null });

        // Normalitzar totes les files
        const normFiles = filesRaw.map((fila) => normalitzarFila(fila));

        // Comprovar els id_rebut únics (només enters > 0) que encara no estiguin al cache
        const idsRebutUnics = Array.from(new Set(
          normFiles
            .map((f) => Number(f.id_rebut))
            .filter((n) => Number.isInteger(n) && n > 0)
        ));
        const pendents = idsRebutUnics.filter((n) => !(n in mapRebuts));

        let nouMap = { ...mapRebuts };
        if(pendents.length > 0){
          setCarregantRebuts(true);
          try{
            const comprovacions = await Promise.all(pendents.map((id) => comprovarRebut(id)));
            const actualitzacio = {};
            pendents.forEach((id, i) => {
              // El resultat (fiable o no) va a nouMap per validar ara amb el missatge adequat;
              // al cache persistent (mapRebuts) només hi van els resultats fiables (true/false),
              // així un error transitori es pot reintentar en una propera pujada.
              nouMap[id] = comprovacions[i];
              if(comprovacions[i] !== null){
                actualitzacio[id] = comprovacions[i];
              }
            });
            setMapRebuts((prev) => ({ ...prev, ...actualitzacio }));
          }finally{
            setCarregantRebuts(false);
          }
        }

        // Validar files amb el mapa
        const validades = normFiles.map((fila, idx) => {
          const { errors, avisos } = validarFila(fila, nouMap);
          return {
            key: idx,
            fila: idx + 2, // +2: fila 1 = capçalera
            dades: fila,
            errors,
            avisos
          };
        });

        setFiles(validades);
        setNomFitxer(file.name);
        setResultats([]);
        setImportantEstat('idle');
        setProgres(0);

        if(validades.length === 0){
          message.warning('El fitxer no conté cap fila de dades.');
        }
      }catch(err){
        console.error('Error llegint Excel:', err);
        message.error('No s\'ha pogut llegir el fitxer Excel.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // evitar upload automàtic d'antd
  };

  // ---------- Importació (només inserts) ----------
  const importar = async () => {
    const filesValides = files.filter(f => f.errors.length === 0);
    if(filesValides.length === 0){
      message.warning('No hi ha cap fila vàlida per importar.');
      return;
    }

    setImportantEstat('processant');
    setProgres(0);
    const resultatsAcumulats = [];

    for(let i = 0; i < filesValides.length; i++){
      const item = filesValides[i];
      const d = item.dades;

      const idRebut = Number(d.id_rebut);
      const dataMoment = parseDataPagament(d.data_pagament);

      const body = {
        import: Number(d.import),
        concepte: entities.encode(String(d.concepte).trim()),
        tipus_pagament: String(d.tipus_pagament).trim().toUpperCase(),
        data_pagament: dataMoment.toISOString()
      };

      let resFila = {
        key: item.key,
        fila: item.fila,
        ok: false,
        id_rebut: idRebut,
        id_retorn: null,
        missatge: ''
      };

      try{
        const resposta = await Api.myFetch('/rebuts/entregat/compte/' + idRebut, 'POST', JSON.stringify(body));
        if(resposta && resposta.result && resposta.result.success === 1){
          const idNou = resposta.data && resposta.data.id;
          resFila.ok = true;
          resFila.id_retorn = idNou;
          resFila.missatge = `Entrada #${idNou} creada (rebut ${idRebut}, ${body.import}€)`;
        }else{
          resFila.ok = false;
          resFila.missatge = `Error insert rebut ${idRebut}: ${(resposta && resposta.data && resposta.data.message) || 'error desconegut'}`;
        }
      }catch(err){
        resFila.ok = false;
        resFila.missatge = `Excepció insert rebut ${idRebut}: ${err.message || err}`;
      }

      resultatsAcumulats.push(resFila);
      setResultats([...resultatsAcumulats]);
      setProgres(Math.round(((i + 1) / filesValides.length) * 100));
    }

    setImportantEstat('acabat');
    const ok = resultatsAcumulats.filter(r => r.ok).length;
    const ko = resultatsAcumulats.length - ok;
    if(ko === 0){
      message.success(`${ok} entrades importades correctament.`, 6);
    }else{
      message.warning(`Importació finalitzada: ${ok} OK, ${ko} amb error.`, 8);
    }
  };

  // ---------- Columnes de les taules ----------
  const nErrors = files.filter(f => f.errors.length > 0).length;
  const nValides = files.filter(f => f.errors.length === 0).length;

  const colsPreview = [
    { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
    {
      title: 'Estat',
      key: 'estat',
      width: 90,
      render: (_, r) => {
        if(r.errors.length > 0) return <Tag color="red">Error</Tag>;
        return <Tag color="green">OK</Tag>;
      }
    },
    { title: 'id_rebut',      dataIndex: ['dades','id_rebut'],      key: 'id_rebut',      width: 100 },
    {
      title: 'import',
      key: 'import',
      width: 100,
      render: (_, r) => esNumeric(r.dades.import)
        ? <span>{Number(r.dades.import).toFixed(2)}€</span>
        : <span>{String(r.dades.import)}</span>
    },
    { title: 'concepte',       dataIndex: ['dades','concepte'],       key: 'concepte' },
    { title: 'tipus_pagament', dataIndex: ['dades','tipus_pagament'], key: 'tipus_pagament', width: 150 },
    {
      title: 'data_pagament',
      key: 'data_pagament',
      width: 130,
      render: (_, r) => {
        const m = parseDataPagament(r.dades.data_pagament);
        return m ? <span>{m.format('DD/MM/YYYY')}</span> : <span>{String(r.dades.data_pagament)}</span>;
      }
    },
    {
      title: 'Missatges',
      key: 'msg',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {r.errors.map((e, i) => <span key={'e'+i} style={{color:'#cf1322'}}>• {e}</span>)}
          {r.avisos.map((a, i) => <span key={'a'+i} style={{color:'#d46b08'}}>• {a}</span>)}
        </Space>
      )
    }
  ];

  const colsResultats = [
    { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
    {
      title: 'Estat',
      key: 'estat',
      width: 70,
      render: (_, r) => r.ok
        ? <CheckCircleTwoTone twoToneColor="#52c41a" />
        : <CloseCircleTwoTone twoToneColor="#cf1322" />
    },
    { title: 'id_rebut',     dataIndex: 'id_rebut',     key: 'id_rebut',     width: 100 },
    { title: 'id entrada',   dataIndex: 'id_retorn',    key: 'id_retorn',    width: 110 },
    { title: 'Missatge',     dataIndex: 'missatge',     key: 'missatge' }
  ];

  const podeImportar =
    files.length > 0 &&
    nValides > 0 &&
    importantEstat !== 'processant' &&
    !carregantRebuts;

  return (
    <div style={{ padding: 20 }}>
      <Row gutter={16} align="middle">
        <Col>
          <Button icon={<DownloadOutlined />} onClick={descarregarPlantilla}>
            {props.t ? props.t('rebuts.import.descarregar_plantilla') || 'Descarregar plantilla' : 'Descarregar plantilla'}
          </Button>
        </Col>
        <Col>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={llegirFitxer}
            showUploadList={false}
            disabled={importantEstat === 'processant' || carregantRebuts}
          >
            <Button icon={<UploadOutlined />} disabled={carregantRebuts}>
              {props.t ? props.t('rebuts.import.triar_fitxer') || 'Triar fitxer Excel' : 'Triar fitxer Excel'}
            </Button>
          </Upload>
        </Col>
        {carregantRebuts && (
          <Col>
            <Spin size="small" /> <span style={{ color: '#555' }}>Comprovant rebuts…</span>
          </Col>
        )}
        {nomFitxer && !carregantRebuts && (
          <Col>
            <span style={{ color: '#555' }}>{nomFitxer}</span>
          </Col>
        )}
      </Row>

      {files.length > 0 && (
        <>
          <Divider />
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col>
              <Alert
                type={nErrors > 0 ? 'error' : 'success'}
                showIcon
                message={
                  <>
                    Total files: <b>{files.length}</b> · Vàlides: <b>{nValides}</b> · Amb error: <b>{nErrors}</b>
                  </>
                }
              />
            </Col>
          </Row>

          <Table
            size="small"
            columns={colsPreview}
            dataSource={files}
            pagination={{ pageSize: 20, position: ['topRight', 'bottomRight'] }}
            scroll={{ x: 1100 }}
            rowClassName={(r) => r.errors.length > 0 ? 'row-error' : ''}
          />

          <Row gutter={16} style={{ marginTop: 16 }} align="middle">
            <Col>
              <Button onClick={props.callback}>
                {props.t ? props.t('generic.back') : 'Enrere'}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={importar}
                loading={importantEstat === 'processant'}
                disabled={!podeImportar}
              >
                Importar {nValides} entrada(es)
              </Button>
            </Col>
            {importantEstat !== 'idle' && (
              <Col flex="auto">
                <Progress percent={progres} status={importantEstat === 'acabat' ? 'success' : 'active'} />
              </Col>
            )}
          </Row>
        </>
      )}

      {resultats.length > 0 && (
        <>
          <Divider>Resultats de la importació</Divider>
          <Table
            size="small"
            columns={colsResultats}
            dataSource={resultats}
            pagination={{ pageSize: 20, position: ['topRight', 'bottomRight'] }}
          />
        </>
      )}

      {files.length === 0 && !carregantRebuts && (
        <Row style={{ marginTop: 20 }}>
          <Col>
            <Button onClick={props.callback}>
              {props.t ? props.t('generic.back') : 'Enrere'}
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default translate(EntregatACompteImportar);
